(() => {
  if (customElements.get('am-campaign-slideshow')) return;

  const saleWindow = (slide) => {
    // Liquid declares legacy/manual dates. Block IDs never imply a sale schedule.
    if (slide.dataset.promotionTag?.trim()) return null;
    const start = slide.dataset.saleStart?.trim();
    const end = slide.dataset.saleEnd?.trim();
    if (slide.hasAttribute('data-sale-start') || slide.hasAttribute('data-sale-end')) return { start, end };
    return null;
  };
  const strictTime = (value) => {
    try { return window.AMPromotions?.parseTag(`am-promo:manual:${value}`)?.endTime ?? NaN; }
    catch { return NaN; }
  };
  const dateEligible = (slide, now) => {
    const sale = saleWindow(slide);
    if (!sale) return true;
    const start = sale.start ? strictTime(sale.start) : -Infinity;
    const end = strictTime(sale.end);
    return !Number.isNaN(start) && Number.isFinite(end) && start <= now && now < end;
  };
  const promotionKey = (slide) => `${slide.dataset.promotionTag?.trim() || ''}\n${slide.dataset.promotionUrl || ''}`;

  const shuffle = (slides) => {
    for (let index = slides.length - 1; index > 0; index--) {
      const other = Math.floor(Math.random() * (index + 1));
      [slides[index], slides[other]] = [slides[other], slides[index]];
    }
    return slides;
  };

  class AMCampaignSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('.am-campaigns__items');
      this.querySelectorAll('[data-slide-clone]').forEach((clone) => clone.remove());
      if (!this.track) return;
      // Keep the original nodes even when excluded; later tag changes can restore them.
      if (!this.originalSlides) {
        this.originalSlides = [...this.track.querySelectorAll('[data-slide-id]')];
        const dots = [...this.querySelectorAll('[data-slide]')];
        this.dotsById = new Map(this.originalSlides.map((slide, index) => [slide.dataset.slideId, dots[index]]));
        this.randomOrder = new Map(shuffle([...this.originalSlides]).map((slide, index) => [slide, index]));
      }
      this.abort = new AbortController();
      const on = (target, event, handler) => target.addEventListener(event, handler, { signal: this.abort.signal });
      this.index = 0;
      this.focused = this.contains(document.activeElement);
      this.editorSelected = false;
      this.animating = false;
      this.pendingIndex = null;
      this.gesture = null;
      this.interval = Math.max(4000, Number(this.dataset.interval) || 7000);
      this.slides = [];
      this.dots = [];
      this.tagStatus = new Map();
      this.refreshGeneration = (this.refreshGeneration || 0) + 1;
      this.controls = this.querySelector('.am-campaigns__controls');
      this.dotsContainer = this.querySelector('.am-campaigns__dots');
      this.section = this.closest('.am-campaigns') || this;
      this.designMode = window.Shopify?.designMode === true;
      this.isGrid = this.dataset.layout === 'grid';
      this.classList.toggle('is-promotion-editor', this.designMode);
      this.renderSlides();

      if (this.isGrid) {
        on(document, 'visibilitychange', () => {
          this.renderSlides();
          if (!document.hidden) this.refreshPromotions();
        });
        on(window, 'pageshow', () => { this.renderSlides(); this.refreshPromotions(); });
        this.refreshPromotions();
        return;
      }

      on(this, 'click', (event) => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-slide')) this.show(Number(button.dataset.slide));
        if (button.dataset.action === 'previous') this.show(this.index - 1);
        if (button.dataset.action === 'next') this.show(this.index + 1);
      });
      on(this, 'keydown', (event) => {
        if (this.slides.length < 2 || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
        event.preventDefault();
        // Keep keyboard focus visible when navigating from a slide's link.
        if (this.track.contains(document.activeElement)) this.querySelector('[data-action="next"]').focus();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      on(this, 'focusin', (event) => {
        this.focused = this.contains(event.target);
        this.schedule();
      });
      on(this, 'focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      });
      on(document, 'visibilitychange', () => {
        this.renderSlides();
        if (!document.hidden) this.refreshPromotions();
        this.schedule();
      });
      on(window, 'pageshow', () => { this.renderSlides(); this.refreshPromotions(); });
      on(this.track, 'transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.finishTransition();
      });

      const viewport = this.querySelector('.am-campaigns__viewport');
      const settleGesture = () => {
        const dragging = this.gesture?.dragging;
        this.gesture = null;
        this.track.style.transition = '';
        if (dragging) {
          this.ignoreClickUntil = Date.now() + 500;
          this.animating = true;
          this.positionTrack(this.index + 1, true);
          this.transitionTimer = setTimeout(() => this.finishTransition(), 1100);
        }
        this.schedule();
      };
      on(viewport, 'pointerdown', (event) => {
        if (this.slides.length < 2 || this.animating || this.gesture || event.isPrimary === false || (event.pointerType !== 'touch' && event.pointerType !== 'pen')) return;
        this.gesture = { x: event.clientX, y: event.clientY, pointerId: event.pointerId, time: event.timeStamp, dragging: false };
        this.schedule();
      });
      on(viewport, 'pointermove', (event) => {
        if (!this.gesture || event.pointerId !== this.gesture.pointerId) return;
        const dx = event.clientX - this.gesture.x;
        const dy = event.clientY - this.gesture.y;
        if (!this.gesture.dragging) {
          if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { settleGesture(); return; }
          if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
          this.gesture.dragging = true;
          viewport.setPointerCapture(event.pointerId);
          this.track.style.transition = 'none';
        }
        const offset = Math.max(-viewport.clientWidth, Math.min(viewport.clientWidth, dx));
        this.track.style.transform = `translateX(calc(-${(this.index + 1) * 100}% + ${offset}px))`;
      });
      on(viewport, 'pointerup', (event) => {
        if (!this.gesture || event.pointerId !== this.gesture.pointerId) return;
        const dx = event.clientX - this.gesture.x;
        const dy = event.clientY - this.gesture.y;
        const flick = Math.abs(dx) > 20 && event.timeStamp - this.gesture.time < 250;
        const threshold = Math.min(60, viewport.clientWidth * 0.15);
        if (this.gesture.dragging) this.ignoreClickUntil = Date.now() + 500;
        if ((Math.abs(dx) > threshold || flick) && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.gesture = null;
          this.track.style.transition = '';
          this.ignoreClickUntil = Date.now() + 500;
          this.show(this.index + (dx < 0 ? 1 : -1));
        } else settleGesture();
      });
      on(viewport, 'pointercancel', (event) => {
        if (event.pointerId === this.gesture?.pointerId) settleGesture();
      });
      on(viewport, 'lostpointercapture', (event) => {
        // Touch starts with implicit capture on the image/link. Its loss bubbles
        // here when capture moves to the viewport; the swipe must keep going.
        if (event.target === viewport && event.pointerId === this.gesture?.pointerId) settleGesture();
      });
      on(viewport, 'pointerleave', () => {
        if (this.gesture && !this.gesture.dragging) settleGesture();
      });
      on(viewport, 'click', (event) => {
        if (Date.now() < (this.ignoreClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      });

      // Custom-element lifecycle handles section reloads without touching other apps.
      on(this, 'shopify:block:select', (event) => {
        const selected = this.slides.findIndex((slide) => slide.dataset.slideId === event.detail.blockId);
        if (selected !== -1) { this.editorSelected = true; this.finishTransition(); this.show(selected); }
      });
      on(this, 'shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); });
      this.refreshPromotions();
    }

    eligible(slide, now) {
      if (this.designMode) return true;
      const tag = slide.dataset.promotionTag?.trim();
      if (!tag) return dateEligible(slide, now);
      try {
        return window.AMPromotions?.isActive(tag, now) === true && this.tagStatus.get(promotionKey(slide)) === true;
      } catch { return false; }
    }

    renderSlides() {
      if (!this.abort || !this.isConnected) return;
      const now = Date.now();
      const nextSlides = this.originalSlides.filter((slide) => this.eligible(slide, now));
      if (!this.isGrid && !this.designMode && this.dataset.shuffle !== 'false') {
        const priority = (slide) => Boolean(slide.dataset.promotionTag?.trim() || saleWindow(slide));
        nextSlides.sort((a, b) => Number(priority(b)) - Number(priority(a)) || this.randomOrder.get(a) - this.randomOrder.get(b));
      }
      this.scheduleBoundary(now);
      if (this.rendered && nextSlides.length === this.slides.length && nextSlides.every((slide, index) => slide === this.slides[index])) return;
      this.rendered = true;
      const activeSlide = this.slides[this.index];
      const focusedElement = document.activeElement;
      const hadFocus = this.contains(focusedElement);
      clearTimeout(this.timer);
      clearTimeout(this.transitionTimer);
      this.pendingIndex = null;
      this.animating = false;
      this.loopReset = false;
      const interruptedGesture = this.gesture;
      this.gesture = null;
      if (interruptedGesture) {
        const viewport = this.querySelector('.am-campaigns__viewport');
        if (interruptedGesture.dragging) this.ignoreClickUntil = Date.now() + 500;
        try { if (viewport.hasPointerCapture(interruptedGesture.pointerId)) viewport.releasePointerCapture(interruptedGesture.pointerId); } catch {}
      }
      this.track.style.transition = 'none';
      this.slides = nextSlides;
      this.index = Math.max(0, this.slides.indexOf(activeSlide));
      this.dots = this.slides.map((slide) => this.dotsById.get(slide.dataset.slideId));
      this.originalSlides.forEach((slide) => {
        slide.hidden = !this.slides.includes(slide);
        slide.inert = true;
        slide.setAttribute('aria-hidden', 'true');
      });
      this.originalSlides.forEach((slide) => {
        if (this.designMode) slide.dataset.promotionPreview = 'editor';
        else delete slide.dataset.promotionPreview;
      });
      this.track.replaceChildren(...this.slides);
      this.dotsContainer?.replaceChildren(...this.dots.filter(Boolean));
      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `${index + 1} of ${this.slides.length}`);
        if (this.dots[index]) this.dots[index].dataset.slide = index;
        slide.querySelectorAll('img').forEach((image) => {
          image.loading = index === this.index ? 'eager' : 'lazy';
          image.fetchPriority = index === this.index ? 'high' : 'auto';
        });
      });
      if (!this.isGrid && this.slides.length > 1) {
        this.track.prepend(this.cloneSlide(this.slides[this.slides.length - 1]));
        this.track.append(this.cloneSlide(this.slides[0]));
      }
      this.classList.toggle('is-ready', !this.isGrid && this.slides.length > 1);
      this.classList.toggle('is-single-slide', this.slides.length === 1);
      this.section.hidden = this.slides.length === 0;
      if (this.controls) this.controls.hidden = this.isGrid || this.slides.length < 2;
      const total = this.querySelector('[data-total-slides]');
      if (total) total.textContent = this.slides.length;
      if (this.slides.length) this.show(this.index, false);
      else {
        this.track.style.transform = '';
        this.track.style.transition = '';
        this.track.setAttribute('aria-live', 'off');
        const current = this.querySelector('[data-current-slide]');
        if (current) current.textContent = '0';
      }
      // Reparenting can blur focused links/dots. Restore only when the user was here.
      if (hadFocus) {
        if (this.contains(focusedElement) && !focusedElement.closest('[hidden], [inert]')) focusedElement.focus({ preventScroll: true });
        else if (this.slides.length) {
          const target = this.slides[this.index].querySelector('a[href], button') || this;
          if (target === this) this.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        } else {
          const host = this.section.closest('.shopify-section');
          if (host) { host.setAttribute('tabindex', '-1'); host.focus({ preventScroll: true }); }
        }
      }
      this.focused = this.contains(document.activeElement);
      this.schedule();
    }

    cloneSlide(slide) {
      const clone = slide.cloneNode(true);
      ['id', 'data-slide-id', 'data-shopify-editor-block', 'data-pinned-slide'].forEach((attribute) => clone.removeAttribute(attribute));
      clone.setAttribute('data-slide-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; image.fetchPriority = 'auto'; });
      return clone;
    }

    scheduleBoundary(now = Date.now()) {
      clearTimeout(this.boundaryTimer);
      if (this.designMode) return;
      let boundary = Infinity;
      for (const slide of this.originalSlides) {
        const tag = slide.dataset.promotionTag?.trim();
        if (tag) {
          try {
            const end = window.AMPromotions?.parseTag(tag)?.endTime;
            if (Number.isFinite(end) && end > now) boundary = Math.min(boundary, end);
          } catch {}
        } else {
          const sale = saleWindow(slide);
          for (const date of [sale?.start, sale?.end]) {
            const time = strictTime(date);
            if (Number.isFinite(time) && time > now) boundary = Math.min(boundary, time);
          }
        }
      }
      if (Number.isFinite(boundary)) this.boundaryTimer = setTimeout(() => this.renderSlides(), Math.min(2147483647, Math.max(1, boundary - now + 5)));
    }

    async refreshPromotions() {
      if (!this.abort || !this.isConnected || this.designMode || this.refreshing) return;
      this.refreshing = true;
      clearTimeout(this.pollTimer);
      const generation = this.refreshGeneration;
      try {
        const checks = new Map();
        for (const slide of this.originalSlides) {
          const tag = slide.dataset.promotionTag?.trim();
          if (tag) checks.set(promotionKey(slide), { tag, url: slide.dataset.promotionUrl || '' });
        }
        const results = await Promise.all([...checks].map(async ([key, value]) => {
          try {
            const active = window.AMPromotions?.isActive(value.tag, Date.now()) === true;
            const confirmed = active && await window.AMPromotions.checkTag(value.tag, value.url, { force: true });
            return [key, confirmed === true];
          } catch { return [key, false]; }
        }));
        if (!this.abort || !this.isConnected || generation !== this.refreshGeneration) return;
        this.tagStatus = new Map(results);
        this.renderSlides();
      } finally {
        if (generation === this.refreshGeneration) {
          this.refreshing = false;
          if (this.abort && this.isConnected) this.pollTimer = setTimeout(() => { this.renderSlides(); this.refreshPromotions(); }, 60000);
        }
      }
    }

    show(index, animate = true) {
      if (!this.slides.length || !Number.isFinite(index)) return;
      if (this.isGrid) {
        this.track.style.transform = '';
        this.track.style.transition = '';
        this.slides.forEach((slide) => {
          slide.inert = false;
          slide.setAttribute('aria-hidden', 'false');
          slide.querySelectorAll('a').forEach((link) => link.removeAttribute('tabindex'));
        });
        this.schedule();
        return;
      }
      if (this.animating) { this.pendingIndex = index; return; }
      const nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      this.animating = this.slides.length > 1 && animate && nextIndex !== this.index;
      this.index = nextIndex;
      this.loopReset = this.slides.length > 1 && (index < 0 || index >= this.slides.length);
      if (this.slides.length > 1) this.positionTrack(index + 1, this.animating);
      else { this.track.style.transform = ''; this.track.style.transition = ''; }
      if (this.animating) this.transitionTimer = setTimeout(() => this.finishTransition(), 1100);
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a').forEach((link) => active ? link.removeAttribute('tabindex') : link.setAttribute('tabindex', '-1'));
        if (active) this.dots[position]?.setAttribute('aria-current', 'true');
        else this.dots[position]?.removeAttribute('aria-current');
      });
      this.querySelector('[data-current-slide]').textContent = this.index + 1;
      this.schedule();
    }

    positionTrack(position, animate) {
      if (!animate) this.track.style.transition = 'none';
      this.track.style.transform = `translateX(-${position * 100}%)`;
      if (!animate) {
        // Commit the identical end frame before restoring the normal transition.
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }
    }

    finishTransition() {
      clearTimeout(this.transitionTimer);
      if (this.loopReset) this.positionTrack(this.index + 1, false);
      this.loopReset = false;
      this.animating = false;
      if (this.pendingIndex !== null) {
        const next = this.pendingIndex;
        this.pendingIndex = null;
        this.show(next);
      }
    }

    schedule() {
      clearTimeout(this.timer);
      const stopped = this.isGrid || this.slides.length < 2 || this.dataset.autoplay === 'false' || this.focused || this.editorSelected || this.gesture || document.hidden;
      this.track.setAttribute('aria-live', stopped ? 'polite' : 'off');
      if (!stopped) this.timer = setTimeout(() => this.show(this.index + 1), this.interval);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.transitionTimer);
      clearTimeout(this.boundaryTimer);
      clearTimeout(this.pollTimer);
      this.refreshGeneration = (this.refreshGeneration || 0) + 1;
      this.refreshing = false;
      this.rendered = false;
      this.abort?.abort();
      this.abort = null;
    }
  }

  customElements.define('am-campaign-slideshow', AMCampaignSlideshow);
})();
